// Site-wide admin-managed banners (top/bottom) + delayed popups.
// Once dismissed by a visitor, stays hidden via localStorage (per message id).
import { useEffect, useState } from "react";
import api from "../lib/api";

const DISMISS_KEY = (id) => `wp:msg_dismissed:${id}`;

export default function SiteMessages({ position }) {
  // position: "top" | "bottom" | "popup"
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/site-messages")
      .then((r) => { if (!cancelled) setMessages(r.data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const visible = messages.filter((m) => {
    if (window.localStorage.getItem(DISMISS_KEY(m.id)) === "1") return false;
    if (position === "popup") return m.kind === "popup";
    if (position === "top") return m.kind === "banner" && (m.position || "top") === "top";
    if (position === "bottom") return m.kind === "banner" && m.position === "bottom";
    return false;
  });

  if (visible.length === 0) return null;

  if (position === "popup") {
    // Render only the first active popup
    return <PopupMessage msg={visible[0]} />;
  }

  return (
    <>
      {visible.map((m) => (
        <BannerMessage key={m.id} msg={m} />
      ))}
    </>
  );
}

// ============ Banner ============
function BannerMessage({ msg }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  const dismiss = () => {
    window.localStorage.setItem(DISMISS_KEY(msg.id), "1");
    setDismissed(true);
  };

  const bg = msg.bg_color || "#5a6e3f";
  const fg = msg.text_color || "#FCF7EC";
  const isBottom = (msg.position || "top") === "bottom";

  // Bottom banners: render fixed above CookieBanner (z-60) so the close button stays clickable.
  const outerClass = isBottom
    ? "fixed bottom-0 left-0 right-0 w-full z-[70] shadow-[0_-6px_24px_rgba(0,0,0,0.18)]"
    : "w-full";

  return (
    <div
      className={outerClass}
      style={{ backgroundColor: bg, color: fg }}
      data-testid={`site-banner-${msg.id}`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-2.5 flex items-center gap-3">
        {msg.image_url && (
          <img src={msg.image_url} alt="" className="h-7 w-7 rounded object-cover shrink-0" />
        )}
        <div className="flex-1 text-sm md:text-base leading-snug">
          {msg.title && <strong className="mr-2">{msg.title}</strong>}
          <span style={{ opacity: 0.95 }}>{msg.message}</span>
        </div>
        {msg.cta_text && msg.cta_link && (
          <a
            href={msg.cta_link}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap"
            style={{ backgroundColor: fg, color: bg }}
            data-testid={`site-banner-cta-${msg.id}`}
          >
            {msg.cta_text} →
          </a>
        )}
        <button
          onClick={dismiss}
          className="p-1.5 rounded-full hover:bg-black/10 transition-colors shrink-0"
          aria-label="Schließen"
          data-testid={`site-banner-close-${msg.id}`}
          style={{ color: fg }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </button>
      </div>
    </div>
  );
}

// ============ Popup ============
function PopupMessage({ msg }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const delay = Math.max(0, Number(msg.delay_seconds) || 7) * 1000;
    const t = setTimeout(() => setOpen(true), delay);
    return () => clearTimeout(t);
  }, [msg.id, msg.delay_seconds]);

  const dismiss = () => {
    window.localStorage.setItem(DISMISS_KEY(msg.id), "1");
    setOpen(false);
  };

  if (!open) return null;
  const bg = msg.bg_color || "#5a6e3f";
  const fg = msg.text_color || "#FCF7EC";

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(26,47,28,0.55)" }}
      onClick={dismiss}
      data-testid={`site-popup-${msg.id}`}
    >
      <div
        className="rounded-3xl max-w-md w-full p-7 md:p-9 text-center relative shadow-2xl"
        style={{ backgroundColor: bg, color: fg }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 p-2 rounded-full hover:bg-black/10 transition-colors"
          aria-label="Schließen"
          data-testid={`site-popup-close-${msg.id}`}
          style={{ color: fg }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </button>
        {msg.image_url && (
          <img src={msg.image_url} alt="" className="mx-auto h-20 w-20 rounded-full object-cover mb-4 border-2" style={{ borderColor: fg }} />
        )}
        {msg.title && (
          <h3 className="font-display text-2xl md:text-3xl mb-2 leading-tight">{msg.title}</h3>
        )}
        {msg.message && (
          <p className="text-sm md:text-base leading-relaxed mb-5" style={{ opacity: 0.92 }}>{msg.message}</p>
        )}
        {msg.cta_text && msg.cta_link && (
          <a
            href={msg.cta_link}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm"
            style={{ backgroundColor: fg, color: bg }}
            data-testid={`site-popup-cta-${msg.id}`}
          >
            {msg.cta_text} →
          </a>
        )}
      </div>
    </div>
  );
}
